import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import SafeIcon from '../common/SafeIcon';
import SearchableSelect from '../components/common/SearchableSelect';

import * as FiIcons from 'react-icons/fi';
import { FaBalanceScale } from 'react-icons/fa';

import {
  transactionService
} from '../services/transactionService';

import {
  receiptService
} from '../services/receiptService';

import {
  coaService
} from '../services/coaService';

import TransactionModal
  from '../components/transactions/TransactionModal';

import TransactionReconciliationPanel
  from '../components/transactions/TransactionReconciliationPanel';

import JournalEntryModal
  from '../components/accounting/JournalEntryModal';

import CsvImportModal
  from '../components/assistant/CsvImportModal';

import TransferMatchNotification
  from '../components/transactions/TransferMatchNotification';

import {
  useClient
} from '../context/ClientContext';

import {
  formatDate
} from '../utils/formatters';

const {
  FiPlus,
  FiSearch,
  FiCheck,
  FiChevronDown,
  FiUpload,
  FiEdit2,
  FiTrash2,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiActivity,
  FiSliders,
  FiRefreshCw,
  FiAlertCircle,
} = FiIcons;

/* =========================================================
   HELPERS
========================================================= */

const normalizeStatus = (
  status
) =>
  String(
    status || ''
  )
    .trim()
    .toLowerCase();

const isReconciledStatus = (
  status
) =>
  normalizeStatus(
    status
  ) === 'reconciled';

const normalizeAccountType = (
  type
) =>
  String(
    type || ''
  )
    .trim()
    .toLowerCase();

const isReconciliationAccount = (
  account
) => {
  if (!account) {
    return false;
  }

  const type =
    normalizeAccountType(
      account.type
    );

  const subType =
    normalizeAccountType(
      account.sub_type
    );

  const name =
    normalizeAccountType(
      account.name
    );

  const isBankOrCash =
    type === 'asset' &&
    (
      subType.includes('bank') ||
      subType.includes('cash') ||
      subType.includes('checking') ||
      subType.includes('savings') ||
      name.includes('bank') ||
      name.includes('cash')
    );

  const isCreditCard =
    type === 'liability' &&
    (
      subType.includes('credit') ||
      name.includes('credit card') ||
      name.includes('creditcard')
    );

  return (
    isBankOrCash ||
    isCreditCard
  );
};

const formatMoney = (
  amount,
  currency = 'USD'
) => {
  const value =
    Number(amount) || 0;

  const normalizedCurrency =
    String(
      currency || 'USD'
    ).toUpperCase();

  try {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency:
          normalizedCurrency,
        minimumFractionDigits: 2
      }
    ).format(value);
  } catch {
    return `${normalizedCurrency} ${value.toFixed(2)}`;
  }
};

const getTransactionAmount = (
  transaction
) => {
  const debit =
    Number(
      transaction.debit
    ) || 0;

  const credit =
    Number(
      transaction.credit
    ) || 0;

  return {
    debit,
    credit,
    amount:
      Math.max(
        debit,
        credit
      ),
    isDebit:
      debit > 0,
    isCredit:
      credit > 0
  };
};

/*
 * Keep date values in YYYY-MM-DD format for
 * the native date input.
 */
const normalizeDateInput = (
  value
) => {
  if (!value) {
    return '';
  }

  const stringValue =
    String(value);

  /*
   * If Supabase already returned:
   * YYYY-MM-DD
   */
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(
      stringValue
    )
  ) {
    return stringValue;
  }

  const date =
    new Date(
      stringValue
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      '0'
    );

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      '0'
    );

  return `${year}-${month}-${day}`;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function Transactions() {
  const {
    activeClient,
    user
  } = useClient();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const organizationId =
    activeClient?.id;

  const organizationCurrency =
    activeClient?.currency ||
    'USD';

  /* =======================================================
     STATE
  ======================================================= */

  const [
    transactions,
    setTransactions
  ] = useState([]);

  const [
    coaList,
    setCoaList
  ] = useState([]);

  const [
    isLoading,
    setIsLoading
  ] = useState(true);

  const [
    isRefreshing,
    setIsRefreshing
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    filters,
    setFilters
  ] = useState({
    search: '',
    accountId: 'all',
    status: 'All',
    reconciledOnly: false
  });

  const [
    selectedTx,
    setSelectedTx
  ] = useState([]);

  const [
    isReconciliationOpen,
    setIsReconciliationOpen
  ] = useState(false);

  const [
    manualReconciliationRequest,
    setManualReconciliationRequest
  ] = useState(null);

  const [
    isModalOpen,
    setIsModalOpen
  ] = useState(false);

  const [
    isJournalModalOpen,
    setIsJournalModalOpen
  ] = useState(false);

  const [
    isBulkUploadModalOpen,
    setIsBulkUploadModalOpen
  ] = useState(false);

  const [
    defaultTxType,
    setDefaultTxType
  ] = useState('Withdrawal');

  const [
    editingTransaction,
    setEditingTransaction
  ] = useState(null);

  /*
   * Currently active inline cell.
   *
   * Examples:
   * transaction-id:date
   * transaction-id:description
   * transaction-id:amount
   * transaction-id:source_account_id
   * transaction-id:account_id
   */
  const [
    editingCell,
    setEditingCell
  ] = useState(null);

  /*
   * Temporary value used while editing text/date/amount.
   *
   * This prevents every keystroke from hitting Supabase.
   */
  const [
    inlineDraft,
    setInlineDraft
  ] = useState('');

  /*
   * Cell currently being persisted.
   */
  const [
    savingCell,
    setSavingCell
  ] = useState(null);

  const [
    isAddMenuOpen,
    setIsAddMenuOpen
  ] = useState(false);

  const [
    isMoreMenuOpen,
    setIsMoreMenuOpen
  ] = useState(false);

  const [
    isAccountMenuOpen,
    setIsAccountMenuOpen
  ] = useState(false);

  const [
    activeMenuId,
    setActiveMenuId
  ] = useState(null);

  const [
    deleteConfirm,
    setDeleteConfirm
  ] = useState({
    isOpen: false,
    transaction: null
  });

  const [
    isProcessing,
    setIsProcessing
  ] = useState(false);

  const menuRef =
    useRef(null);

  const addMenuRef =
    useRef(null);

  const moreMenuRef =
    useRef(null);

  const accountMenuRef =
    useRef(null);

  const viewStateRef =
    useRef(null);

  const fileInputRef =
    useRef(null);

  const inlineInputRef =
    useRef(null);

  /* =======================================================
     VIEW STATE
  ======================================================= */

  const captureViewState = () => {
    if (
      typeof window ===
      'undefined'
    ) {
      return;
    }

    viewStateRef.current = {
      x: window.scrollX,
      y: window.scrollY
    };
  };

  const restoreViewState = () => {
    if (
      typeof window ===
        'undefined' ||
      !viewStateRef.current
    ) {
      return;
    }

    const {
      x,
      y
    } = viewStateRef.current;

    requestAnimationFrame(() => {
      window.scrollTo(
        x,
        y
      );

      requestAnimationFrame(() => {
        window.scrollTo(
          x,
          y
        );
      });
    });
  };

  /* =======================================================
     LOAD DATA
  ======================================================= */

  const loadData = async ({
    preserveView = false,
    silent = false
  } = {}) => {
    if (!organizationId) {
      setTransactions([]);
      setCoaList([]);
      setIsLoading(false);
      return;
    }

    if (preserveView) {
      captureViewState();
    }

    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    setError('');

    try {
      const [
        accountData,
        transactionData
      ] = await Promise.all([
        coaService.getChartOfAccounts(
          organizationId
        ),

        transactionService.getTransactions(
          {},
          organizationId
        )
      ]);

      setCoaList(
        accountData || []
      );

      setTransactions(
        transactionData || []
      );

      setSelectedTx(
        current => {
          const validIds =
            new Set(
              (
                transactionData ||
                []
              ).map(
                transaction =>
                  transaction.id
              )
            );

          return current.filter(
            id =>
              validIds.has(
                id
              )
          );
        }
      );

      if (preserveView) {
        restoreViewState();
      }
    } catch (
      loadError
    ) {
      console.error(
        'Failed to load General Ledger:',
        loadError
      );

      setError(
        loadError?.message ||
        'Unable to load transactions.'
      );
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [organizationId]);

  /* =======================================================
     ORGANIZATION SWITCH SAFETY
  ======================================================= */

  useEffect(() => {
    setTransactions([]);
    setSelectedTx([]);

    setEditingCell(null);
    setInlineDraft('');
    setSavingCell(null);

    setFilters({
      search: '',
      accountId: 'all',
      status: 'All',
      reconciledOnly: false
    });

    setIsReconciliationOpen(false);
    setManualReconciliationRequest(null);
  }, [organizationId]);

  /* =======================================================
     RECONCILIATION DEEP LINK
  ======================================================= */

  useEffect(() => {
    const request =
      location.state;

    if (
      !request?.openReconciliation ||
      !request?.manualReconciliation ||
      !request?.accountId ||
      !request?.statementDate ||
      request?.endingBalance ===
        undefined
    ) {
      return;
    }

    setManualReconciliationRequest({
      accountId:
        request.accountId,

      statementDate:
        request.statementDate,

      endingBalance:
        request.endingBalance,

      reconciliationId:
        request.reconciliationId ||
        null
    });

    setFilters(
      current => ({
        ...current,
        accountId:
          request.accountId
      })
    );

    setIsReconciliationOpen(
      true
    );

    navigate(
      location.pathname,
      {
        replace: true,
        state: null
      }
    );
  }, [
    location.pathname,
    location.state,
    navigate
  ]);

  /* =======================================================
     CLOSE MENUS
  ======================================================= */

  useEffect(() => {
    const handleClickOutside =
      event => {
        if (
          menuRef.current &&
          !menuRef.current.contains(
            event.target
          )
        ) {
          setActiveMenuId(
            null
          );
        }

        if (
          addMenuRef.current &&
          !addMenuRef.current.contains(
            event.target
          )
        ) {
          setIsAddMenuOpen(
            false
          );
        }

        if (
          moreMenuRef.current &&
          !moreMenuRef.current.contains(
            event.target
          )
        ) {
          setIsMoreMenuOpen(
            false
          );
        }

        if (
          accountMenuRef.current &&
          !accountMenuRef.current.contains(
            event.target
          )
        ) {
          setIsAccountMenuOpen(
            false
          );
        }
      };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  /* =======================================================
     ACCOUNTS
  ======================================================= */

  const reconciliationAccounts =
    useMemo(
      () =>
        coaList.filter(
          isReconciliationAccount
        ),
      [coaList]
    );

  const activeAccounts =
    useMemo(
      () =>
        coaList.filter(
          account =>
            account.is_active !==
            false
        ),
      [coaList]
    );

  /*
   * IMPORTANT:
   *
   * SearchableSelect uses option.id
   * as its selected value.
   *
   * Therefore these options intentionally
   * use id instead of value.
   */

  const sourceAccountOptions =
    useMemo(
      () =>
        reconciliationAccounts.map(
          account => ({
            id:
              account.id,

            label:
              account.code
                ? `${account.code} · ${account.name}`
                : account.name,

            code:
              account.code,

            name:
              account.name,

            ...account
          })
        ),
      [
        reconciliationAccounts
      ]
    );

  const categoryOptions =
    useMemo(
      () =>
        activeAccounts.map(
          account => ({
            id:
              account.id,

            label:
              account.code
                ? `${account.code} · ${account.name}`
                : account.name,

            code:
              account.code,

            name:
              account.name,

            ...account
          })
        ),
      [activeAccounts]
    );

  const groupedAccounts =
    useMemo(() => {
      return reconciliationAccounts.reduce(
        (
          groups,
          account
        ) => {
          const group =
            normalizeAccountType(
              account.type
            ) ===
            'liability'
              ? 'Credit cards'
              : 'Bank and cash';

          groups[group] = [
            ...(groups[group] ||
              []),
            account
          ];

          return groups;
        },
        {}
      );
    }, [
      reconciliationAccounts
    ]);

  /* =======================================================
     ACCOUNT BALANCES
  ======================================================= */

  const accountBalances =
    useMemo(() => {
      return transactions.reduce(
        (
          balances,
          transaction
        ) => {
          const accountId =
            transaction.source_account_id;

          if (!accountId) {
            return balances;
          }

          const debit =
            Number(
              transaction.debit
            ) || 0;

          const credit =
            Number(
              transaction.credit
            ) || 0;

          balances[accountId] =
            (
              balances[accountId] ||
              0
            ) +
            credit -
            debit;

          return balances;
        },
        {}
      );
    }, [
      transactions
    ]);

  const getDisplayBalance = (
    accountId
  ) => {
    const account =
      coaList.find(
        item =>
          item.id ===
          accountId
      );

    const rawBalance =
      accountBalances[
        accountId
      ] || 0;

    if (
      normalizeAccountType(
        account?.type
      ) ===
      'liability'
    ) {
      return -rawBalance;
    }

    return rawBalance;
  };

  const totalBalance =
    useMemo(() => {
      return reconciliationAccounts.reduce(
        (
          total,
          account
        ) =>
          total +
          getDisplayBalance(
            account.id
          ),
        0
      );
    }, [
      reconciliationAccounts,
      accountBalances,
      coaList
    ]);

  const selectedAccount =
    useMemo(() => {
      if (
        filters.accountId ===
        'all'
      ) {
        return {
          name:
            'All accounts',

          balance:
            totalBalance,

          currency:
            organizationCurrency
        };
      }

      const account =
        coaList.find(
          item =>
            item.id ===
            filters.accountId
        );

      return {
        name:
          account?.name ||
          'Unknown account',

        balance:
          getDisplayBalance(
            filters.accountId
          ),

        currency:
          account?.currency ||
          organizationCurrency
      };
    }, [
      filters.accountId,
      coaList,
      totalBalance,
      organizationCurrency,
      accountBalances
    ]);

  /* =======================================================
     RUNNING BALANCE
  ======================================================= */

  const runningBalances =
    useMemo(() => {
      if (
        filters.accountId ===
        'all'
      ) {
        return {};
      }

      const account =
        coaList.find(
          item =>
            item.id ===
            filters.accountId
        );

      const isLiability =
        normalizeAccountType(
          account?.type
        ) ===
        'liability';

      let runningBalance =
        0;

      const accountTransactions =
        transactions
          .filter(
            transaction =>
              transaction.source_account_id ===
              filters.accountId
          )
          .sort(
            (
              a,
              b
            ) => {
              const dateDifference =
                new Date(
                  `${a.date}T00:00:00`
                ).getTime() -
                new Date(
                  `${b.date}T00:00:00`
                ).getTime();

              if (
                dateDifference !==
                0
              ) {
                return dateDifference;
              }

              return (
                new Date(
                  a.created_at ||
                    0
                ).getTime() -
                new Date(
                  b.created_at ||
                    0
                ).getTime()
              );
            }
          );

      return accountTransactions.reduce(
        (
          balances,
          transaction
        ) => {
          const debit =
            Number(
              transaction.debit
            ) || 0;

          const credit =
            Number(
              transaction.credit
            ) || 0;

          const change =
            credit -
            debit;

          runningBalance +=
            isLiability
              ? -change
              : change;

          balances[
            transaction.id
          ] =
            runningBalance;

          return balances;
        },
        {}
      );
    }, [
      transactions,
      coaList,
      filters.accountId
    ]);

  /* =======================================================
     FILTERED TRANSACTIONS
  ======================================================= */

  const filteredTransactions =
    useMemo(() => {
      const search =
        filters.search
          .trim()
          .toLowerCase();

      return transactions
        .filter(
          transaction => {
            if (
              filters.accountId !==
                'all' &&
              transaction.source_account_id !==
                filters.accountId
            ) {
              return false;
            }

            if (
              filters.status !==
                'All' &&
              normalizeStatus(
                transaction.status
              ) !==
                normalizeStatus(
                  filters.status
                )
            ) {
              return false;
            }

            if (
              filters.reconciledOnly &&
              !isReconciledStatus(
                transaction.status
              )
            ) {
              return false;
            }

            if (!search) {
              return true;
            }

            const searchable = [
              transaction.description,

              transaction.account?.name,
              transaction.account?.code,

              transaction.source_account?.name,
              transaction.source_account?.code,

              transaction.source,
              transaction.status
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            return searchable.includes(
              search
            );
          }
        )
        .sort(
          (
            a,
            b
          ) =>
            new Date(
              b.date
            ).getTime() -
            new Date(
              a.date
            ).getTime()
        );
    }, [
      transactions,
      filters
    ]);

  /* =======================================================
     SUMMARY
  ======================================================= */

  const ledgerSummary =
    useMemo(() => {
      return filteredTransactions.reduce(
        (
          summary,
          transaction
        ) => {
          summary.debit +=
            Number(
              transaction.debit
            ) || 0;

          summary.credit +=
            Number(
              transaction.credit
            ) || 0;

          if (
            isReconciledStatus(
              transaction.status
            )
          ) {
            summary.reconciled +=
              1;
          }

          return summary;
        },
        {
          debit: 0,
          credit: 0,
          reconciled: 0
        }
      );
    }, [
      filteredTransactions
    ]);

  /* =======================================================
     SELECTION
  ======================================================= */

  const allVisibleSelected =
    filteredTransactions.length >
      0 &&
    filteredTransactions.every(
      transaction =>
        selectedTx.includes(
          transaction.id
        )
    );

  const handleSelectAll =
    event => {
      if (
        event.target.checked
      ) {
        setSelectedTx(
          filteredTransactions.map(
            transaction =>
              transaction.id
          )
        );
      } else {
        setSelectedTx([]);
      }
    };

  const handleSelectRow =
    transactionId => {
      setSelectedTx(
        current =>
          current.includes(
            transactionId
          )
            ? current.filter(
                id =>
                  id !==
                  transactionId
              )
            : [
                ...current,
                transactionId
              ]
      );
    };

  /* =======================================================
     TOAST
  ======================================================= */

  const showToast = (
    message,
    type = 'success'
  ) => {
    const toast =
      document.createElement(
        'div'
      );

    toast.className =
      `fixed bottom-5 right-5 ${
        type === 'success'
          ? 'bg-slate-900'
          : 'bg-rose-600'
      } text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-xl z-[9999]`;

    toast.textContent =
      message;

    document.body.appendChild(
      toast
    );

    setTimeout(
      () =>
        toast.remove(),
      3000
    );
  };

  /* =======================================================
     REFRESH
  ======================================================= */

  const handleRefresh =
    async () => {
      await loadData({
        preserveView: true,
        silent: true
      });

      showToast(
        'Transactions refreshed'
      );
    };

  /* =======================================================
     ADD MENU
  ======================================================= */

  const handleAddAction =
    action => {
      setIsAddMenuOpen(
        false
      );

      if (
        action === 'deposit' ||
        action === 'withdrawal'
      ) {
        setDefaultTxType(
          action === 'deposit'
            ? 'Deposit'
            : 'Withdrawal'
        );

        setEditingTransaction(
          null
        );

        setIsModalOpen(
          true
        );
      }

      if (
        action === 'receipt'
      ) {
        fileInputRef.current?.click();
      }

      if (
        action === 'journal'
      ) {
        setIsJournalModalOpen(
          true
        );
      }
    };

  /* =======================================================
     EDIT MODAL
  ======================================================= */

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  /* =======================================================
     INLINE EDITING
  ======================================================= */

  const getCellKey = (
    transactionId,
    field
  ) =>
    `${transactionId}:${field}`;

  /*
   * Start editing a text/date/amount cell.
   */
  const startInlineValueEdit = (
    transaction,
    field
  ) => {
    if (
      savingCell ||
      !transaction?.id
    ) {
      return;
    }

    let initialValue = '';

    if (
      field === 'date'
    ) {
      initialValue =
        normalizeDateInput(
          transaction.date
        );
    }

    if (
      field === 'description'
    ) {
      initialValue =
        transaction.description ||
        '';
    }

    if (
      field === 'amount'
    ) {
      const {
        amount
      } =
        getTransactionAmount(
          transaction
        );

      initialValue =
        amount > 0
          ? String(amount)
          : '';
    }

    setInlineDraft(
      initialValue
    );

    setEditingCell(
      getCellKey(
        transaction.id,
        field
      )
    );

    requestAnimationFrame(() => {
      inlineInputRef.current?.focus();

      /*
       * Select the entire value for
       * quick replacement.
       */
      inlineInputRef.current?.select?.();
    });
  };

  /*
   * Cancel text/date/amount editing
   * without touching the database.
   */
  const cancelInlineValueEdit =
    () => {
      if (savingCell) {
        return;
      }

      setEditingCell(null);
      setInlineDraft('');
    };

  /*
   * Persist Date, Description, or Amount.
   */
  const handleInlineValueSave =
    async (
      transaction,
      field
    ) => {
      if (
        !transaction?.id ||
        !organizationId
      ) {
        return;
      }

      const cellKey =
        getCellKey(
          transaction.id,
          field
        );

      if (
        savingCell ===
        cellKey
      ) {
        return;
      }

      let updates = {};
      let previousValue;

      /* -----------------------------------------------
         DATE
      ------------------------------------------------ */

      if (
        field === 'date'
      ) {
        const nextDate =
          String(
            inlineDraft || ''
          ).trim();

        previousValue =
          normalizeDateInput(
            transaction.date
          );

        /*
         * Date is required.
         */
        if (!nextDate) {
          setInlineDraft(
            previousValue
          );

          setEditingCell(
            null
          );

          return;
        }

        if (
          previousValue ===
          nextDate
        ) {
          setEditingCell(
            null
          );

          setInlineDraft('');
          return;
        }

        updates = {
          date:
            nextDate
        };
      }

      /* -----------------------------------------------
         DESCRIPTION
      ------------------------------------------------ */

      if (
        field ===
        'description'
      ) {
        const nextDescription =
          String(
            inlineDraft ?? ''
          );

        previousValue =
          transaction.description ||
          '';

        if (
          previousValue ===
          nextDescription
        ) {
          setEditingCell(
            null
          );

          setInlineDraft('');
          return;
        }

        updates = {
          description:
            nextDescription
        };
      }

      /* -----------------------------------------------
         AMOUNT
      ------------------------------------------------ */

      if (
        field === 'amount'
      ) {
        const {
          debit,
          credit,
          isDebit,
          isCredit
        } =
          getTransactionAmount(
            transaction
          );

        previousValue =
          Math.max(
            debit,
            credit
          );

        const normalizedAmount =
          String(
            inlineDraft ?? ''
          )
            .trim()
            .replace(
              /,/g,
              ''
            );

        const nextAmount =
          normalizedAmount === ''
            ? 0
            : Number(
                normalizedAmount
              );

        if (
          !Number.isFinite(
            nextAmount
          ) ||
          nextAmount < 0
        ) {
          showToast(
            'Please enter a valid amount.',
            'error'
          );

          setInlineDraft(
            previousValue
              ? String(
                  previousValue
                )
              : ''
          );

          return;
        }

        if (
          Number(
            previousValue
          ) ===
          Number(
            nextAmount
          )
        ) {
          setEditingCell(
            null
          );

          setInlineDraft('');
          return;
        }

        /*
         * Preserve the original side.
         *
         * Debit transaction:
         * debit = amount
         * credit = 0
         *
         * Credit transaction:
         * debit = 0
         * credit = amount
         *
         * For an existing zero-value transaction,
         * default to debit.
         */
        if (
          isCredit &&
          !isDebit
        ) {
          updates = {
            debit: 0,
            credit:
              nextAmount
          };
        } else {
          updates = {
            debit:
              nextAmount,
            credit: 0
          };
        }
      }

      setSavingCell(
        cellKey
      );

      captureViewState();

      try {
        const updatedTransaction =
          await transactionService.updateTransaction(
            transaction.id,
            organizationId,
            updates
          );

        /*
         * Merge the returned transaction
         * into the current row.
         *
         * This is intentionally not a
         * full reload.
         */
        setTransactions(
          current =>
            current.map(
              item =>
                item.id ===
                transaction.id
                  ? {
                      ...item,
                      ...updatedTransaction
                    }
                  : item
            )
        );

        setEditingCell(
          null
        );

        setInlineDraft('');

        restoreViewState();

        showToast(
          'Transaction updated'
        );
      } catch (
        updateError
      ) {
        console.error(
          `Failed to update ${field}:`,
          updateError
        );

        /*
         * The row was never changed
         * optimistically, so the
         * original database value
         * remains visible.
         */
        showToast(
          updateError?.message ||
            'Unable to update transaction.',
          'error'
        );
      } finally {
        setSavingCell(
          null
        );
      }
    };

  /*
   * Enter = save.
   * Escape = cancel.
   */
  const handleInlineInputKeyDown =
    (
      event,
      transaction,
      field
    ) => {
      if (
        event.key ===
        'Enter'
      ) {
        event.preventDefault();

        event.currentTarget.blur();
        return;
      }

      if (
        event.key ===
        'Escape'
      ) {
        event.preventDefault();

        cancelInlineValueEdit();
      }
    };

  /*
   * Source Account / Category.
   *
   * These use SearchableSelect and
   * save immediately after selection.
   */
  const handleInlineAccountChange =
    async (
      transaction,
      field,
      value
    ) => {
      if (
        !transaction?.id ||
        !organizationId
      ) {
        return;
      }

      const previousValue =
        transaction[field] ||
        null;

      const nextValue =
        value || null;

      /*
       * Nothing changed.
       */
      if (
        previousValue ===
        nextValue
      ) {
        setEditingCell(
          null
        );

        return;
      }

      const cellKey =
        getCellKey(
          transaction.id,
          field
        );

      setSavingCell(
        cellKey
      );

      captureViewState();

      try {
        /*
         * Only this field is sent.
         *
         * Category:
         * account_id
         *
         * Source:
         * source_account_id
         */
        const updatedTransaction =
          await transactionService.updateTransaction(
            transaction.id,
            organizationId,
            {
              [field]:
                nextValue
            }
          );

        setTransactions(
          current =>
            current.map(
              item =>
                item.id ===
                transaction.id
                  ? {
                      ...item,
                      ...updatedTransaction
                    }
                  : item
            )
        );

        setEditingCell(
          null
        );

        restoreViewState();

        showToast(
          'Transaction updated'
        );
      } catch (
        updateError
      ) {
        console.error(
          `Failed to update ${field}:`,
          updateError
        );

        /*
         * Because we don't optimistically
         * modify the row, the previous
         * value remains intact.
         */
        showToast(
          updateError?.message ||
            'Unable to update transaction.',
          'error'
        );
      } finally {
        setSavingCell(
          null
        );
      }
    };

  /* =======================================================
     DELETE
  ======================================================= */

  const executeDelete =
    async () => {
      const transaction =
        deleteConfirm.transaction;

      if (!transaction) {
        return;
      }

      setIsProcessing(
        true
      );

      setDeleteConfirm({
        isOpen: false,
        transaction: null
      });

      captureViewState();

      try {
        await transactionService.deleteTransaction(
          transaction.id,
          organizationId
        );

        await loadData({
          preserveView: true
        });

        showToast(
          'Transaction deleted'
        );
      } catch (
        deleteError
      ) {
        console.error(
          deleteError
        );

        showToast(
          deleteError?.message ||
            'Unable to delete transaction.',
          'error'
        );
      } finally {
        setIsProcessing(
          false
        );
      }
    };

  /* =======================================================
     RECONCILIATION
  ======================================================= */

  const handleReconciliationCompleted =
    async account => {
      await loadData({
        preserveView: true
      });

      setFilters(
        current => ({
          ...current,
          accountId:
            account.id,
          reconciledOnly:
            true
        })
      );

      showToast(
        `${account.name} reconciled successfully`
      );
    };

  /* =======================================================
     RECEIPT UPLOAD
  ======================================================= */

  const handleReceiptUpload =
    async event => {
      const file =
        event.target.files?.[0];

      if (!file) {
        return;
      }

      setIsProcessing(
        true
      );

      try {
        const uploadResult =
          await receiptService.uploadReceiptFile(
            file
          );

        await receiptService.createReceipt(
          uploadResult
        );

        navigate(
          '/receipts'
        );

        showToast(
          'Receipt uploaded'
        );
      } catch (
        uploadError
      ) {
        console.error(
          uploadError
        );

        showToast(
          uploadError?.message ||
            'Upload failed',
          'error'
        );
      } finally {
        setIsProcessing(
          false
        );

        event.target.value =
          '';
      }
    };

  /* =======================================================
     RENDER
  ======================================================= */

  const showRunningBalance =
    filters.accountId !==
    'all';

  return (
    <div className="mx-auto max-w-[1500px] space-y-3 px-0 py-1">

      {/* HIDDEN RECEIPT INPUT */}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,application/pdf"
        onChange={
          handleReceiptUpload
        }
      />

      {/* HEADER */}

      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">

        <div>
          <h1 className="flex items-center gap-2.5 text-[22px] font-medium tracking-tight text-slate-900 md:text-[24px]">

            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white shadow-sm">
              <SafeIcon
                icon={FiActivity}
                className="text-[18px]"
              />
            </span>

            Transactions
          </h1>

          <p className="mt-1 text-[12px] text-slate-500">
            Posted accounting transactions for the active organization.
          </p>
        </div>

        <div className="flex items-center gap-2">

          <button
            type="button"
            onClick={
              handleRefresh
            }
            disabled={
              isRefreshing
            }
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            <SafeIcon
              icon={
                FiRefreshCw
              }
              className={
                isRefreshing
                  ? 'animate-spin'
                  : ''
              }
            />

            Refresh
          </button>

          <div
            className="relative"
            ref={addMenuRef}
          >

            <button
              onClick={() =>
                setIsAddMenuOpen(
                  !isAddMenuOpen
                )
              }
              className="flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2.5 text-[12px] font-medium text-white shadow-sm transition hover:bg-slate-800"
            >
              <SafeIcon
                icon={FiPlus}
              />

              Add transaction

              <SafeIcon
                icon={
                  FiChevronDown
                }
              />
            </button>

            {isAddMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-52 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl">

                <button
                  onClick={() =>
                    handleAddAction(
                      'deposit'
                    )
                  }
                  className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <SafeIcon
                    icon={
                      FiArrowDownLeft
                    }
                    className="text-emerald-600"
                  />
                  Deposit
                </button>

                <button
                  onClick={() =>
                    handleAddAction(
                      'withdrawal'
                    )
                  }
                  className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <SafeIcon
                    icon={
                      FiArrowUpRight
                    }
                    className="text-rose-600"
                  />
                  Withdrawal
                </button>

                <button
                  onClick={() =>
                    handleAddAction(
                      'receipt'
                    )
                  }
                  className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <SafeIcon
                    icon={FiUpload}
                    className="text-blue-600"
                  />
                  Scan receipt
                </button>

                <button
                  onClick={() =>
                    handleAddAction(
                      'journal'
                    )
                  }
                  className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <SafeIcon
                    icon={FiEdit2}
                    className="text-slate-600"
                  />
                  Journal entry
                </button>

              </div>
            )}

          </div>

          <div
            className="relative"
            ref={moreMenuRef}
          >

            <button
              onClick={() =>
                setIsMoreMenuOpen(
                  !isMoreMenuOpen
                )
              }
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
            >
              More

              <SafeIcon
                icon={
                  FiChevronDown
                }
              />
            </button>

            {isMoreMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl">

                <button
                  onClick={() => {
                    setIsMoreMenuOpen(
                      false
                    );

                    navigate(
                      '/connect-bank'
                    );
                  }}
                  className="w-full px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Connect bank
                </button>

                <button
                  onClick={() => {
                    setIsMoreMenuOpen(
                      false
                    );

                    setIsBulkUploadModalOpen(
                      true
                    );
                  }}
                  className="w-full px-3.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Upload CSV
                </button>

              </div>
            )}

          </div>

        </div>
      </div>

      {/* ERROR */}

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-700">

          <SafeIcon
            icon={
              FiAlertCircle
            }
            className="mt-0.5 shrink-0"
          />

          <div>
            <p className="font-medium">
              Unable to load the General Ledger.
            </p>

            <p className="mt-0.5">
              {error}
            </p>
          </div>

        </div>
      )}

      {/* ACCOUNT + RECONCILIATION */}

      <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">

        {/* ACCOUNT SELECTOR */}

        <div
          className="relative"
          ref={
            accountMenuRef
          }
        >

          <button
            onClick={() =>
              setIsAccountMenuOpen(
                !isAccountMenuOpen
              )
            }
            className="flex min-h-[62px] w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-left shadow-sm hover:border-slate-300"
          >

            <div>

              <p className="text-[13px] font-medium text-slate-900">
                {
                  selectedAccount.name
                }
              </p>

              <p className="mt-0.5 text-[14px] font-medium text-slate-600">
                {formatMoney(
                  selectedAccount.balance,
                  selectedAccount.currency
                )}
              </p>

            </div>

            <SafeIcon
              icon={
                FiChevronDown
              }
              className={`text-[18px] text-slate-400 ${
                isAccountMenuOpen
                  ? 'rotate-180'
                  : ''
              }`}
            />

          </button>

          {isAccountMenuOpen && (
            <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">

              <button
                onClick={() => {
                  setFilters(
                    current => ({
                      ...current,
                      accountId:
                        'all'
                    })
                  );

                  setIsAccountMenuOpen(
                    false
                  );
                }}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50"
              >

                <span className="text-[12px] font-medium text-slate-800">
                  All accounts
                </span>

                {filters.accountId ===
                  'all' && (
                  <SafeIcon
                    icon={
                      FiCheck
                    }
                    className="text-blue-600"
                  />
                )}

              </button>

              <div className="max-h-64 overflow-y-auto">

                {Object.entries(
                  groupedAccounts
                ).map(
                  ([
                    group,
                    accounts
                  ]) => (
                    <div
                      key={group}
                    >

                      <p className="bg-slate-50 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
                        {group}
                      </p>

                      {accounts.map(
                        account => (
                          <button
                            key={
                              account.id
                            }
                            onClick={() => {
                              setFilters(
                                current => ({
                                  ...current,
                                  accountId:
                                    account.id
                                })
                              );

                              setIsAccountMenuOpen(
                                false
                              );
                            }}
                            className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50"
                          >

                            <span className="text-[12px] font-medium text-slate-800">
                              {account.code
                                ? `${account.code} · `
                                : ''}

                              {
                                account.name
                              }
                            </span>

                            <span className="flex items-center gap-3 text-[12px] text-slate-500">

                              {formatMoney(
                                getDisplayBalance(
                                  account.id
                                ),
                                account.currency ||
                                  organizationCurrency
                              )}

                              {filters.accountId ===
                                account.id && (
                                <SafeIcon
                                  icon={
                                    FiCheck
                                  }
                                  className="text-blue-600"
                                />
                              )}

                            </span>

                          </button>
                        )
                      )}

                    </div>
                  )
                )}

              </div>

              <button
                onClick={() =>
                  navigate(
                    '/accounting/chart-of-accounts'
                  )
                }
                className="w-full border-t border-slate-100 px-4 py-2.5 text-left text-[12px] font-medium text-blue-600 hover:bg-blue-50"
              >
                <SafeIcon
                  icon={FiPlus}
                  className="mr-2 inline"
                />

                Add a new account
              </button>

            </div>
          )}

        </div>

        {/* RECONCILIATION */}

        <button
          type="button"
          role="switch"
          aria-checked={
            isReconciliationOpen
          }
          onClick={() => {
            setIsReconciliationOpen(
              !isReconciliationOpen
            );

            if (
              isReconciliationOpen
            ) {
              setManualReconciliationRequest(
                null
              );
            }
          }}
          className={`flex min-h-[62px] items-center justify-between rounded-xl border px-4 py-2.5 text-left shadow-sm transition ${
            isReconciliationOpen
              ? 'border-blue-400 bg-blue-50/60'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >

          <span className="flex items-center gap-3">

            <span
              className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                isReconciliationOpen
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              <SafeIcon
                icon={
                  FiSliders
                }
                className="text-[17px]"
              />
            </span>

            <span>

              <span className="block text-[13px] font-medium text-slate-900">
                Reconciliation
              </span>

              <span className="mt-0.5 block text-[11px] text-slate-500">
                {isReconciliationOpen
                  ? 'Statement setup is open'
                  : 'Start a bank statement reconciliation'}
              </span>

            </span>

          </span>

          <span
            className={`relative inline-flex h-6 w-11 items-center rounded-full p-1 ${
              isReconciliationOpen
                ? 'bg-blue-600'
                : 'bg-slate-300'
            }`}
          >

            <span
              className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                isReconciliationOpen
                  ? 'translate-x-5'
                  : 'translate-x-0'
              }`}
            />

          </span>

        </button>

      </div>

      {/* RECONCILIATION PANEL */}

      {isReconciliationOpen && (
        <TransactionReconciliationPanel
          organizationId={
            organizationId
          }

          organizationCurrency={
            organizationCurrency
          }

          accounts={
            coaList
          }

          groupedAccounts={
            groupedAccounts
          }

          entries={
            transactions
          }

          selectedAccountId={
            filters.accountId !==
            'all'
              ? filters.accountId
              : null
          }

          initialManualReconciliation={
            manualReconciliationRequest
          }

          onAccountChange={
            accountId => {
              setFilters(
                current => ({
                  ...current,
                  accountId
                })
              );
            }
          }

          onCompleted={
            handleReconciliationCompleted
          }

          onManualReconciliationComplete={() => {
            setManualReconciliationRequest(
              null
            );

            setIsReconciliationOpen(
              false
            );
          }}

          onBackToHistory={() =>
            setIsReconciliationOpen(
              false
            )
          }

          onViewHistory={() =>
            setIsReconciliationOpen(
              false
            )
          }
        />
      )}

      {/* SEARCH / FILTERS */}

      <div className="flex flex-col gap-2 sm:flex-row">

        <div className="relative flex-1">

          <SafeIcon
            icon={FiSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400"
          />

          <input
            value={
              filters.search
            }
            onChange={
              event =>
                setFilters(
                  current => ({
                    ...current,
                    search:
                      event.target.value
                  })
                )
            }
            placeholder="Search transactions..."
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-[13px] text-slate-700 outline-none shadow-sm focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10"
          />

        </div>

        <select
          value={
            filters.status
          }
          onChange={
            event =>
              setFilters(
                current => ({
                  ...current,
                  status:
                    event.target.value
                })
              )
          }
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[12px] text-slate-700 outline-none"
        >

          <option value="All">
            All statuses
          </option>

          <option value="Cleared">
            Cleared
          </option>

          <option value="Reconciled">
            Reconciled
          </option>

          <option value="Pending">
            Pending
          </option>

        </select>

        <button
          type="button"
          onClick={() =>
            setFilters(
              current => ({
                ...current,
                reconciledOnly:
                  !current.reconciledOnly
              })
            )
          }
          className={`rounded-xl border px-3 py-2.5 text-[12px] font-medium transition ${
            filters.reconciledOnly
              ? 'border-blue-300 bg-blue-50 text-blue-700'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          Reconciled only
        </button>

      </div>

      {/* SUMMARY */}

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">

          <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
            Transactions
          </p>

          <p className="mt-1 text-lg font-semibold text-slate-900">
            {
              filteredTransactions.length
            }
          </p>

        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">

          <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
            Debits
          </p>

          <p className="mt-1 text-lg font-semibold text-slate-900">
            {formatMoney(
              ledgerSummary.debit,
              organizationCurrency
            )}
          </p>

        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">

          <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
            Credits
          </p>

          <p className="mt-1 text-lg font-semibold text-slate-900">
            {formatMoney(
              ledgerSummary.credit,
              organizationCurrency
            )}
          </p>

        </div>

      </div>

      {/* TRANSFER MATCH */}

      <TransferMatchNotification
        accountId={
          organizationId
        }
        refreshKey={
          transactions.length
        }
        onUpdated={() =>
          loadData({
            preserveView: true
          })
        }
      />

      {/* LEDGER TABLE */}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

        <div className="overflow-x-auto">

          <table className="w-full min-w-[1250px] border-collapse text-left">

            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">

                <th className="w-11 px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={
                      allVisibleSelected
                    }
                    onChange={
                      handleSelectAll
                    }
                    aria-label="Select all transactions"
                  />
                </th>

                <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Date
                </th>

                <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Description
                </th>

                <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Source Account
                </th>

                <th className="px-3 py-2.5 text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Category
                </th>

                <th className="px-3 py-2.5 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Debit
                </th>

                <th className="px-3 py-2.5 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                  Credit
                </th>

                {showRunningBalance && (
                  <th className="px-3 py-2.5 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                    Balance
                  </th>
                )}

                <th className="w-16 px-2 py-2.5" />

              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">

              {/* LOADING */}

              {isLoading &&
                Array.from({
                  length: 7
                }).map(
                  (_, index) => (
                    <tr
                      key={
                        index
                      }
                      className="animate-pulse"
                    >
                      <td
                        colSpan={
                          showRunningBalance
                            ? 9
                            : 8
                        }
                        className="px-4 py-4"
                      >
                        <div className="h-4 rounded bg-slate-100" />
                      </td>
                    </tr>
                  )
                )}

              {/* EMPTY */}

              {!isLoading &&
                filteredTransactions.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={
                        showRunningBalance
                          ? 9
                          : 8
                      }
                      className="px-4 py-14 text-center text-[11px] font-medium uppercase tracking-[0.1em] text-slate-400"
                    >
                      No transactions found
                    </td>
                  </tr>
                )}

              {/* TRANSACTIONS */}

              {!isLoading &&
                filteredTransactions.map(
                  transaction => {
                    const {
                      debit,
                      credit
                    } =
                      getTransactionAmount(
                        transaction
                      );

                    const category =
                      transaction.account;

                    const sourceAccount =
                      transaction.source_account;

                    const currency =
                      category?.currency ||
                      sourceAccount?.currency ||
                      organizationCurrency;

                    const reconciled =
                      isReconciledStatus(
                        transaction.status
                      );

                    /* -----------------------------------
                       CELL KEYS
                    ----------------------------------- */

                    const dateCellKey =
                      getCellKey(
                        transaction.id,
                        'date'
                      );

                    const descriptionCellKey =
                      getCellKey(
                        transaction.id,
                        'description'
                      );

                    const amountCellKey =
                      getCellKey(
                        transaction.id,
                        'amount'
                      );

                    const sourceCellKey =
                      getCellKey(
                        transaction.id,
                        'source_account_id'
                      );

                    const categoryCellKey =
                      getCellKey(
                        transaction.id,
                        'account_id'
                      );

                    /* -----------------------------------
                       EDITING STATES
                    ----------------------------------- */

                    const isDateEditing =
                      editingCell ===
                      dateCellKey;

                    const isDescriptionEditing =
                      editingCell ===
                      descriptionCellKey;

                    const isAmountEditing =
                      editingCell ===
                      amountCellKey;

                    const isSourceEditing =
                      editingCell ===
                      sourceCellKey;

                    const isCategoryEditing =
                      editingCell ===
                      categoryCellKey;

                    /* -----------------------------------
                       SAVING STATES
                    ----------------------------------- */

                    const isDateSaving =
                      savingCell ===
                      dateCellKey;

                    const isDescriptionSaving =
                      savingCell ===
                      descriptionCellKey;

                    const isAmountSaving =
                      savingCell ===
                      amountCellKey;

                    const isSourceSaving =
                      savingCell ===
                      sourceCellKey;

                    const isCategorySaving =
                      savingCell ===
                      categoryCellKey;

                    const isAnyCellSaving =
                      Boolean(
                        savingCell
                      );

                    return (
                      <tr
                        key={
                          transaction.id
                        }
                        className={`group transition-colors hover:bg-slate-50 ${
                          selectedTx.includes(
                            transaction.id
                          )
                            ? 'bg-blue-50/50'
                            : ''
                        }`}
                      >

                        {/* CHECKBOX */}

                        <td
                          className="px-4 py-3"
                          onClick={
                            event =>
                              event.stopPropagation()
                          }
                        >

                          <input
                            type="checkbox"
                            checked={selectedTx.includes(
                              transaction.id
                            )}
                            onChange={() =>
                              handleSelectRow(
                                transaction.id
                              )
                            }
                          />

                        </td>

                        {/* DATE */}

                        <td className="whitespace-nowrap px-3 py-3 text-[12px] text-slate-500">

                          {isDateEditing ? (
                            <div className="relative min-w-[145px]">

                              <input
                                ref={
                                  inlineInputRef
                                }
                                type="date"
                                value={
                                  inlineDraft
                                }
                                disabled={
                                  isDateSaving
                                }
                                onChange={
                                  event =>
                                    setInlineDraft(
                                      event
                                        .target
                                        .value
                                    )
                                }
                                onBlur={() =>
                                  handleInlineValueSave(
                                    transaction,
                                    'date'
                                  )
                                }
                                onKeyDown={event =>
                                  handleInlineInputKeyDown(
                                    event,
                                    transaction,
                                    'date'
                                  )
                                }
                                className="w-full rounded-md border border-blue-300 bg-white px-2 py-1.5 text-[12px] text-slate-700 outline-none ring-4 ring-blue-500/10 disabled:opacity-60"
                              />

                              {isDateSaving && (
                                <SafeIcon
                                  icon={
                                    FiRefreshCw
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[12px] text-blue-600"
                                />
                              )}

                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={() =>
                                startInlineValueEdit(
                                  transaction,
                                  'date'
                                )
                              }
                              className="group/date inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-left transition hover:bg-blue-50 disabled:cursor-wait"
                            >

                              {reconciled && (
                                <span
                                  title="Reconciled"
                                  className="text-emerald-600"
                                >
                                  <SafeIcon
                                    icon={
                                      FaBalanceScale
                                    }
                                    className="text-[13px]"
                                  />
                                </span>
                              )}

                              <span className="text-slate-500 group-hover/date:text-blue-600">
                                {formatDate(
                                  transaction.date
                                )}
                              </span>

                            </button>
                          )}

                        </td>

                        {/* DESCRIPTION */}

                        <td className="max-w-[300px] px-3 py-3">

                          {isDescriptionEditing ? (
                            <div className="relative min-w-[230px]">

                              <input
                                ref={
                                  inlineInputRef
                                }
                                type="text"
                                value={
                                  inlineDraft
                                }
                                disabled={
                                  isDescriptionSaving
                                }
                                autoComplete="off"
                                onChange={
                                  event =>
                                    setInlineDraft(
                                      event
                                        .target
                                        .value
                                    )
                                }
                                onBlur={() =>
                                  handleInlineValueSave(
                                    transaction,
                                    'description'
                                  )
                                }
                                onKeyDown={event =>
                                  handleInlineInputKeyDown(
                                    event,
                                    transaction,
                                    'description'
                                  )
                                }
                                className="w-full rounded-md border border-blue-300 bg-white px-2.5 py-1.5 text-[13px] font-medium text-slate-800 outline-none ring-4 ring-blue-500/10 disabled:opacity-60"
                              />

                              {isDescriptionSaving && (
                                <SafeIcon
                                  icon={
                                    FiRefreshCw
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[12px] text-blue-600"
                                />
                              )}

                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={() =>
                                startInlineValueEdit(
                                  transaction,
                                  'description'
                                )
                              }
                              className="block max-w-[300px] rounded-md px-1 py-1 text-left disabled:cursor-wait"
                              title={
                                transaction.description ||
                                'No description'
                              }
                            >

                              <span className="block truncate text-[13px] font-medium text-slate-800 hover:text-blue-600">
                                {transaction.description ||
                                  'No description'}
                              </span>

                            </button>
                          )}

                        </td>

                        {/* SOURCE ACCOUNT */}

                        <td className="px-3 py-3">

                          {isSourceEditing ? (

                            <div
                              className="min-w-[210px]"
                              onClick={(event) =>
                                event.stopPropagation()
                              }
                            >

                              <SearchableSelect
                                options={
                                  sourceAccountOptions
                                }

                                value={
                                  transaction.source_account_id ||
                                  ''
                                }

                                onChange={(value) =>
                                  handleInlineAccountChange(
                                    transaction,
                                    'source_account_id',
                                    value
                                  )
                                }

                                placeholder="Select source account"

                                searchPlaceholder="Search source accounts..."

                                emptyMessage="No source accounts found"

                                disabled={
                                  Boolean(savingCell) ||
                                  !organizationId
                                }

                                loading={
                                  isSourceSaving
                                }

                                getLabel={(option) =>
                                  option?.label ||
                                  option?.name ||
                                  ''
                                }

                                getSearchText={(option) =>
                                  [
                                    option?.code,
                                    option?.name,
                                    option?.label
                                  ]
                                    .filter(Boolean)
                                    .join(' ')
                                }
                              />

                            </div>

                          ) : (

                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={() =>
                                startInlineValueEdit(
                                  transaction,
                                  'source_account_id'
                                )
                              }
                              className="group flex flex-col rounded-md px-1 py-1 text-left disabled:cursor-wait"
                            >

                              <span className="flex items-center gap-2 text-[12px] font-medium text-slate-800 group-hover:text-blue-600">

                                {sourceAccount?.name ||
                                  'Unknown source account'}

                                {isSourceSaving && (
                                  <SafeIcon
                                    icon={FiRefreshCw}
                                    className="animate-spin text-[12px] text-blue-600"
                                  />
                                )}

                              </span>

                              {sourceAccount?.code && (
                                <span className="mt-0.5 text-[10px] text-slate-400">
                                  {sourceAccount.code}
                                </span>
                              )}

                            </button>

                          )}

                        </td>

                        {/* CATEGORY */}

                        <td className="px-3 py-3">

                          {isCategoryEditing ? (
                            <div
                              className="min-w-[220px]"
                              onClick={(event) =>
                                event.stopPropagation()
                              }
                            >

                              <SearchableSelect
                                options={categoryOptions}

                                value={
                                  transaction.account_id ||
                                  ''
                                }

                                onChange={(value) =>
                                  handleInlineAccountChange(
                                    transaction,
                                    'account_id',
                                    value
                                  )
                                }

                                placeholder="Select category"

                                searchPlaceholder="Search categories..."

                                emptyMessage="No categories found"

                                disabled={
                                  Boolean(savingCell) ||
                                  !organizationId
                                }

                                loading={
                                  isCategorySaving
                                }

                                getLabel={(option) =>
                                  option?.label ||
                                  option?.name ||
                                  ''
                                }

                                getSearchText={(option) =>
                                  [
                                    option?.code,
                                    option?.name,
                                    option?.label
                                  ]
                                    .filter(Boolean)
                                    .join(' ')
                                }
                              />

                            </div>
                          ) : (

                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={(event) => {
                                event.stopPropagation();

                                startInlineValueEdit(
                                  transaction,
                                  'account_id'
                                );
                              }}
                              className="group flex w-full items-center gap-2 rounded-md px-1 py-1 text-left disabled:cursor-wait"
                            >

                              <span className="min-w-0 truncate">

                                {category ? (
                                  <>
                                    {category.code && (
                                      <span className="font-medium text-slate-800">
                                        {category.code}
                                      </span>
                                    )}

                                    <span className="ml-1 text-[12px] text-slate-600 group-hover:text-blue-600">
                                      {category.code
                                        ? `· ${category.name}`
                                        : category.name}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-slate-400">
                                    Uncategorized
                                  </span>
                                )}

                              </span>

                              {!isAnyCellSaving && (
                                <SafeIcon
                                  icon={FiEdit2}
                                  className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
                                />
                              )}

                            </button>

                          )}

                        </td>

                        {/* DEBIT */}

                        <td className="whitespace-nowrap px-3 py-3 text-right text-[13px] font-medium text-slate-800">

                          {isAmountEditing ? (
                            <div className="relative ml-auto w-[130px]">

                              <input
                                ref={
                                  inlineInputRef
                                }
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={
                                  inlineDraft
                                }
                                disabled={
                                  isAmountSaving
                                }
                                onChange={
                                  event =>
                                    setInlineDraft(
                                      event
                                        .target
                                        .value
                                    )
                                }
                                onBlur={() =>
                                  handleInlineValueSave(
                                    transaction,
                                    'amount'
                                  )
                                }
                                onKeyDown={event =>
                                  handleInlineInputKeyDown(
                                    event,
                                    transaction,
                                    'amount'
                                  )
                                }
                                className="w-full rounded-md border border-blue-300 bg-white px-2 py-1.5 text-right text-[12px] font-medium text-slate-800 outline-none ring-4 ring-blue-500/10 disabled:opacity-60"
                              />

                              {isAmountSaving && (
                                <SafeIcon
                                  icon={
                                    FiRefreshCw
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[12px] text-blue-600"
                                />
                              )}

                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={() =>
                                startInlineValueEdit(
                                  transaction,
                                  'amount'
                                )
                              }
                              className="ml-auto block rounded-md px-1 py-1 text-right disabled:cursor-wait"
                            >

                              {debit > 0
                                ? (
                                  <span className="hover:text-blue-600">
                                    {formatMoney(
                                      debit,
                                      currency
                                    )}
                                  </span>
                                )
                                : '—'}

                            </button>
                          )}

                        </td>

                        {/* CREDIT */}

                        <td className="whitespace-nowrap px-3 py-3 text-right text-[13px] font-medium text-emerald-600">

                          {!isAmountEditing && (
                            <button
                              type="button"
                              disabled={
                                isAnyCellSaving
                              }
                              onClick={() =>
                                startInlineValueEdit(
                                  transaction,
                                  'amount'
                                )
                              }
                              className="ml-auto block rounded-md px-1 py-1 text-right disabled:cursor-wait"
                            >

                              {credit > 0
                                ? (
                                  <span className="hover:text-blue-600">
                                    {formatMoney(
                                      credit,
                                      currency
                                    )}
                                  </span>
                                )
                                : '—'}

                            </button>
                          )}

                          {isAmountEditing && (
                            <span className="text-slate-300">
                              —
                            </span>
                          )}

                        </td>

                        {/* BALANCE */}

                        {showRunningBalance && (
                          <td className="whitespace-nowrap px-3 py-3 text-right text-[13px] font-medium text-slate-800">

                            {formatMoney(
                              runningBalances[
                                transaction.id
                              ] || 0,
                              currency
                            )}

                          </td>
                        )}

                        {/* ACTIONS */}

                        <td
                          className="relative px-2 py-3"
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          <div
                            className="flex justify-center"
                            ref={
                              activeMenuId === transaction.id
                                ? menuRef
                                : null
                            }
                          >
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                setActiveMenuId((current) =>
                                  current === transaction.id
                                    ? null
                                    : transaction.id
                                );
                              }}
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                              aria-label="Transaction actions"
                              aria-expanded={
                                activeMenuId === transaction.id
                              }
                            >
                              <SafeIcon
                                icon={FiChevronDown}
                                className={`text-[14px] transition-transform ${
                                  activeMenuId === transaction.id
                                    ? 'rotate-180'
                                    : ''
                                }`}
                              />
                            </button>

                            {activeMenuId === transaction.id && (
                              <div
                                className="absolute right-2 top-12 z-[60] w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl"
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                {/* EDIT */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleEdit(transaction);
                                    setActiveMenuId(null);
                                  }}
                                  className="flex w-full items-center px-3.5 py-2.5 text-left text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
                                >
                                  <SafeIcon
                                    icon={FiEdit2}
                                    className="mr-2.5 text-[13px] text-slate-500"
                                  />

                                  Edit transaction
                                </button>

                                {/* DELETE */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDeleteConfirm({
                                      isOpen: true,
                                      transaction
                                    });

                                    setActiveMenuId(null);
                                  }}
                                  className="flex w-full items-center px-3.5 py-2.5 text-left text-[11px] font-medium text-rose-600 transition-colors hover:bg-rose-50"
                                >
                                  <SafeIcon
                                    icon={FiTrash2}
                                    className="mr-2.5 text-[13px]"
                                  />

                                  Delete transaction
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                      </tr>
                    );
                  }
                )}

            </tbody>

          </table>

        </div>

      </div>

      {/* TRANSACTION MODAL */}

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTransaction(null);
        }}
        initialData={editingTransaction}
        defaultType={defaultTxType}
        onSuccess={async () => {
          captureViewState();

          await loadData({
            preserveView: true,
          });

          setIsModalOpen(false);
          setEditingTransaction(null);

          showToast('Transaction saved');
        }}
      />

      {/* JOURNAL */}

      <JournalEntryModal
        isOpen={
          isJournalModalOpen
        }
        onClose={() =>
          setIsJournalModalOpen(
            false
          )
        }
        onSuccess={
          async () => {
            captureViewState();

            await loadData({
              preserveView: true
            });

            setIsJournalModalOpen(
              false
            );

            showToast(
              'Journal entry saved'
            );
          }
        }
      />

      {/* CSV */}

      <CsvImportModal
        isOpen={
          isBulkUploadModalOpen
        }
        onClose={() =>
          setIsBulkUploadModalOpen(
            false
          )
        }
        organizationId={
          organizationId
        }
        userId={
          user?.id
        }
        organizationName={
          activeClient?.name
        }
        organizationCurrency={
          organizationCurrency
        }
        onUploadComplete={
          async () => {
            captureViewState();

            await loadData({
              preserveView: true
            });

            setIsBulkUploadModalOpen(
              false
            );

            showToast(
              'Transactions imported'
            );
          }
        }
      />

      {/* DELETE CONFIRMATION */}

      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">

          <div className="w-full max-w-sm rounded-2xl bg-white p-5 text-center shadow-2xl">

            <SafeIcon
              icon={
                FiAlertCircle
              }
              className="mx-auto mb-3 text-3xl text-rose-600"
            />

            <h3 className="text-[17px] font-medium text-slate-900">
              Delete transaction?
            </h3>

            <p className="mb-5 mt-2 text-[13px] text-slate-500">
              This posted transaction will be removed from the General Ledger.
            </p>

            <div className="flex gap-2.5">

              <button
                onClick={() =>
                  setDeleteConfirm({
                    isOpen:
                      false,
                    transaction:
                      null
                  })
                }
                className="flex-1 rounded-lg bg-slate-100 py-2.5 text-[12px] font-medium text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>

              <button
                onClick={
                  executeDelete
                }
                disabled={
                  isProcessing
                }
                className="flex-1 rounded-lg bg-rose-600 py-2.5 text-[12px] font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                Delete
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}